import api from "../../../../services/api";
import { getApiResponse, handleApiError } from "../helpers/apiResponse";

export const listKnowledge = async (params = {}) => {
  try {
    const body = {
      page: params.page ?? 1,
      page_size: params.page_size ?? 20,
      order_by: params.order_by ?? "updated_at",
      order_direction: params.order_direction ?? "DESC",
      ...(params.search && params.search.keyword && { search: params.search }),
    };

    // Add filters if provided
    const filters = {};
    if (params.filters) {
      if (params.filters.department_uuid) {
        filters.department_uuid = params.filters.department_uuid;
      }
      if (params.filters.user_uuid) {
        filters.user_uuid = params.filters.user_uuid;
      }
      if (params.filters.modified_from) {
        filters.modified_from = params.filters.modified_from;
      }
      if (params.filters.modified_to) {
        filters.modified_to = params.filters.modified_to;
      }
    }

    // Only add filters object if it has at least one filter
    if (Object.keys(filters).length > 0) {
      body.filters = filters;
    }

    const response = await api.post("/v1/knowledgebase/list", body);
    const responseData = response.data;

    // Use helper to normalize response and handle errors
    // This handles: HTTP 200 with statusCode 40X in body (backend error 400-499)
    // and HTTP 500+ errors
    const normalizedResponse = getApiResponse(
      responseData,
      response,
      'An error occurred while listing knowledge bases.'
    );

    // Ensure body is always an array for list endpoints
    return {
      body: Array.isArray(normalizedResponse.body) ? normalizedResponse.body : [],
      meta: normalizedResponse.meta || {},
      status_code: normalizedResponse.status_code,
    };

  } catch (error) {
    // Use helper to normalize error handling
    // This handles both API errors (with response.data) and network errors
    throw handleApiError(error, 'An error occurred while listing knowledge bases.');
  }
};

/**
 * Create a new knowledge base
 * 
 * @param {Object} params - Knowledge base data
 * @param {string} params.name - Knowledge base name
 * @param {string} params.department_uuid - Department UUID
 * @param {string} params.description - Description (optional, defaults to "-")
 * @param {string} params.owner_uuid - Owner UUID (optional)
 * @returns {Promise<Object>} Created knowledge base data with body and status_code
 */
export const createKnowledge = async (params = {}) => {
  try {
    const body = {
      name: params.name,
      department_uuid: params.department_uuid,
      description: params.description || '-',
    };

    // Add owner_uuid if provided
    if (params.owner_uuid) {
      body.owner_uuid = params.owner_uuid;
    }

    const response = await api.post("/v1/knowledgebase/create", body);
    const responseData = response.data;

    // Use helper to normalize response and handle errors
    // This handles: HTTP 200 with statusCode 40X in body (backend error 400-499)
    // and HTTP 500+ errors
    const normalizedResponse = getApiResponse(
      responseData,
      response,
      'An error occurred while creating knowledge base.'
    );

    return {
      body: normalizedResponse.body || {},
      meta: normalizedResponse.meta || null,
      status_code: normalizedResponse.status_code,
    };

  } catch (error) {
    // Use helper to normalize error handling
    // This handles both API errors (with response.data) and network errors
    throw handleApiError(error, 'An error occurred while creating knowledge base.');
  }
};

/**
 * Update an existing knowledge base
 * 
 * @param {Object} params - Knowledge base data
 * @param {string} params.knowledgebase_uuid - Knowledge base UUID (required)
 * @param {string} params.name - Knowledge base name
 * @param {string} params.department_uuid - Department UUID
 * @param {string} params.owner_uuid - Owner UUID (optional)
 * @returns {Promise<Object>} Updated knowledge base data with body and status_code
 */
export const updateKnowledge = async (params = {}) => {
  try {
    if (!params.knowledgebase_uuid) {
      throw new Error('knowledgebase_uuid is required');
    }

    const body = {
      knowledgebase_uuid: params.knowledgebase_uuid,
    };

    // Add name if provided
    if (params.name) {
      body.name = params.name;
    }

    // Add department_uuid if provided
    if (params.department_uuid) {
      body.department_uuid = params.department_uuid;
    }

    // Add owner_uuid if provided
    if (params.owner_uuid) {
      body.owner_uuid = params.owner_uuid;
    }

    const response = await api.post("/v1/knowledgebase/update", body);
    const responseData = response.data;

    // Use helper to normalize response and handle errors
    // This handles: HTTP 200 with statusCode 40X in body (backend error 400-499)
    // and HTTP 500+ errors
    const normalizedResponse = getApiResponse(
      responseData,
      response,
      'An error occurred while updating knowledge base.'
    );

    return {
      body: normalizedResponse.body || {},
      meta: normalizedResponse.meta || null,
      status_code: normalizedResponse.status_code,
    };

  } catch (error) {
    // Use helper to normalize error handling
    // This handles both API errors (with response.data) and network errors
    throw handleApiError(error, 'An error occurred while updating knowledge base.');
  }
};

/**
 * Archive/Delete a knowledge base
 * 
 * @param {Object} params - Knowledge base data
 * @param {string} params.knowledgebase_uuid - Knowledge base UUID to archive/delete
 * @returns {Promise<Object>} Response with status_code and msgCode
 */
export const archiveKnowledge = async (params = {}) => {
  try {
    if (!params.knowledgebase_uuid) {
      throw new Error('knowledgebase_uuid is required');
    }

    const body = {
      knowledgebase_uuid: params.knowledgebase_uuid,
    };

    const response = await api.post("/v1/knowledgebase/delete", body);
    const responseData = response.data;

    // Use helper to normalize response and handle errors
    // This handles: HTTP 200 with statusCode 40X in body (backend error 400-499)
    // and HTTP 500+ errors
    const normalizedResponse = getApiResponse(
      responseData,
      response,
      'An error occurred while archiving knowledge base.'
    );

    return {
      body: normalizedResponse.body || null,
      meta: normalizedResponse.meta || null,
      status_code: normalizedResponse.status_code,
      msg_code: normalizedResponse.msg_code || [],
    };

  } catch (error) {
    // Use helper to normalize error handling
    // This handles both API errors (with response.data) and network errors
    throw handleApiError(error, 'An error occurred while archiving knowledge base.');
  }
};

/**
 * Get a knowledge base by UUID
 * 
 * @param {Object} params - Knowledge base parameters
 * @param {string} params.knowledgebase_uuid - Knowledge base UUID to retrieve
 * @returns {Promise<Object>} Response with knowledge base data, status_code, and msg_code
 */
export const getKnowledgeByUuid = async (params = {}) => {
  try {
    if (!params.knowledgebase_uuid) {
      throw new Error('knowledgebase_uuid is required');
    }

    const response = await api.get(`/v1/knowledgebase/${params.knowledgebase_uuid}`);
    const responseData = response.data;

    // Use helper to normalize response and handle errors
    // This handles: HTTP 200 with statusCode 40X in body (backend error 400-499)
    // and HTTP 500+ errors
    const normalizedResponse = getApiResponse(
      responseData,
      response,
      'An error occurred while retrieving knowledge base.'
    );

    return {
      body: normalizedResponse.body || {},
      meta: normalizedResponse.meta || null,
      status_code: normalizedResponse.status_code,
      msg_code: normalizedResponse.msg_code || [],
    };

  } catch (error) {
    // Use helper to normalize error handling
    // This handles both API errors (with response.data) and network errors
    console.warn("API call failed, falling back to mock data:", error.message);

    try {
      const mockResponse = await fetch("/mock-knowledge-base-detail.json");
      const mockData = await mockResponse.json();

      return {
        body: mockData.body || {},
        meta: mockData.meta || null,
        status_code: mockData.statusCode || 200,
        msg_code: mockData.msgCode || [],
      };
    } catch (mockError) {
      console.error("Error fetching mock knowledge base detail:", mockError);
      throw handleApiError(error, 'An error occurred while retrieving knowledge base.');
    }
  }
};

/**
 * Batch attach file(s) to document(s)
 * 
 * @param {Object} params - Attach file parameters
 * @param {Array} params.items - Array of items to attach
 * @param {string} [params.items[].file_id] - File ID (optional)
 * @param {string} [params.items[].s3_key] - S3 key (optional)
 * @param {string} params.items[].document_uuid - Document UUID (required)
 * @returns {Promise<Object>} Response with attached files data, status_code, and msg_code
 */
export const attachFiletoKnowledge = async (params = {}) => {
  try {
    if (!params.items || !Array.isArray(params.items) || params.items.length === 0) {
      throw new Error('items array is required and must not be empty');
    }

    // Validate that each item has document_uuid
    const invalidItems = params.items.filter(item => !item.document_uuid);
    if (invalidItems.length > 0) {
      throw new Error('Each item must have a document_uuid');
    }

    const body = {
      items: params.items.map(item => ({
        ...(item.file_id && { file_id: item.file_id }),
        ...(item.s3_key && { s3_key: item.s3_key }),
        document_uuid: item.document_uuid,
      })),
    };

    const response = await api.post("/v1/knowledgebase/file/attach", body);
    const responseData = response.data;

    // Use helper to normalize response and handle errors
    // This handles: HTTP 200 with statusCode 40X in body (backend error 400-499)
    // and HTTP 500+ errors
    const normalizedResponse = getApiResponse(
      responseData,
      response,
      'An error occurred while attaching files to knowledge base.'
    );

    return {
      body: normalizedResponse.body || {},
      meta: normalizedResponse.meta || null,
      status_code: normalizedResponse.status_code,
      msg_code: normalizedResponse.msg_code || [],
    };

  } catch (error) {
    // Use helper to normalize error handling
    // This handles both API errors (with response.data) and network errors
    throw handleApiError(error, 'An error occurred while attaching files to knowledge base.');
  }
};

